import { Grid, Typography, useEventCallback } from '@mui/material';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { CancellationAireVM } from 'cancellations/view-models/cancellation-aire';
import ForeignStateList, { ForeignStateVM } from 'system/components/shared/foreign-state-list';
import { FormikErrors, FormikTouched } from 'formik';
import InitiativeAireList from './initiative-list';
import SubeventAireList from './subevent-list';
import ArticleList from 'mutation/components/registration-citizenship/form/citizenship/form/shared/article-list';
import { ItemVM } from 'shell/view-models/item';
import { EventCancellationAire } from 'cancellations/constants/event-cancellation-citizenship';
import { SelectChangeEvent } from '@mui/material';
import { MotArtCitizenship } from 'shell/models/mutation/mot-art-citizenship';

interface CancellationAireFormProps {
  values: CancellationAireVM | undefined;
  touched?: FormikTouched<CancellationAireVM>;
  errors?: FormikErrors<CancellationAireVM>;
  isLossCitizenship?: boolean;
  onChange: (
    key: keyof CancellationAireVM,
    value: CancellationAireVM[keyof CancellationAireVM]
  ) => void;
}

const CancellationAireFormComponent = ({
  values,
  touched,
  errors,
  isLossCitizenship,
  onChange,
}: CancellationAireFormProps): React.ReactElement => {
  const handleForeignStateListChange = useEventCallback((foreignState?: ForeignStateVM | null) => {
    onChange('newCitizenship', foreignState);
  });

  const onChangeArticleList = useEventCallback((value: ItemVM<MotArtCitizenship> | null) => {
    onChange('opeLegis', value);
  });

  const onChangeInitiativeList = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typeInitiative', event.target.value);
  });

  const onChangeSubEvent = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('subevent', event.target.value);
  });

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography>{"Viene cancellato/a dall'AIRE in data"}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data cancellazione AIRE *"
            aria-label="Data cancellazione AIRE"
            value={values?.cancellationDate || null}
            onChange={(date) => onChange('cancellationDate', date)}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="cancellationDate"
                fullWidth
                error={touched?.cancellationDate && Boolean(errors?.cancellationDate)}
                helperText={errors?.cancellationDate}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={10} md={2}>
          <Typography>{'Per inizativa'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <InitiativeAireList
            value={values?.typeInitiative || ''}
            onChange={onChangeInitiativeList}
            required
            errors={touched?.typeInitiative && Boolean(errors?.typeInitiative)}
          />
        </Grid>
      </Grid>
      {isLossCitizenship ? (
        <>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography>{'subevento perdita cittadinanza'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <SubeventAireList
                value={values?.subevent || ''}
                onChange={onChangeSubEvent}
                required
                errors={touched?.subevent && Boolean(errors?.subevent)}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography>{'perde la cittadinanza italiana dal'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data perdita cittadinanza *"
                aria-label="Data perdita cittadinanza"
                value={values?.lossCitizenshipDate || null}
                onChange={(date) => onChange('lossCitizenshipDate', date)}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="lossCitizenshipDate"
                    fullWidth
                    error={touched?.lossCitizenshipDate && Boolean(errors?.lossCitizenshipDate)}
                    helperText={errors?.lossCitizenshipDate}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography>{'nuova cittadinanza'}</Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <ForeignStateList
                label="Nuova cittadinanza*"
                aria-label="Nuova cittadinanza"
                value={values?.newCitizenship?.id || ''}
                onChange={handleForeignStateListChange}
                error={touched?.newCitizenship && Boolean(errors?.newCitizenship)}
              />
            </Grid>
          </Grid>
          {values?.subevent === EventCancellationAire.LossCitizenshipOpeLegis ? (
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography>{'riferimento “OPE LEGIS” '}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <ArticleList
                  value={values?.opeLegis?.id?.toString() || ''}
                  onChange={onChangeArticleList}
                  idReason={values?.subevent}
                />
              </Grid>
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

CancellationAireFormComponent.displayName = 'CancellationAire form component';

const CancellationAireFormMemo = memo(CancellationAireFormComponent);

export default CancellationAireFormMemo;
